import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import WebViewer from "@pdftron/webviewer";
import { FileText, WarningCircle } from "@phosphor-icons/react";
import {
  applyApryseFeatureRestrictions,
  getEmailPreviewUrl,
  getPreviewFileInfo,
  getPresignedDocumentUrl,
  isLargeStreamedPdf,
} from "../../../components/Document/DocumentUtils";
import {
  fetchDocumentAnnotations,
  saveDocumentAnnotations,
} from "../../../service/matter";
import MediaPreview from "../../../components/Document/MediaPreview";
import {
  landOnCitation,
  subscribeCitationJump,
} from "../../../utils/citationJump";
import { releaseOffscreenPageMemory } from "../../../utils/releaseOffscreenPageMemory";
import { startInboxOpen } from "../inboxOpenStopwatch";
import {
  InboxViewerBody,
  InboxViewerDocName,
  InboxViewerEmpty,
  InboxViewerEmptyDescription,
  InboxViewerEmptyTitle,
  InboxViewerPane,
  InboxViewerToolbar,
} from "../InboxStyled";

/**
 * Middle-column document viewer for the Attorney Inbox.
 *
 * Hosts a single Apryse `WebViewer` instance and swaps documents in via
 * `loadDocument()` whenever `documentId` changes — instead of re-mounting
 * the viewer for each row click. This is the same pattern used by
 * `MailIntake/AssignView`, which we mirrored deliberately because:
 *
 *   - Apryse's WASM bootstrap is 1–3 seconds. Re-mounting it on every
 *     row click would make inbox triage feel sluggish.
 *   - The container <div> survives the document switch; only the
 *     internal viewer state is replaced.
 *
 * The init effect uses a callback-ref-backed state so it re-runs as
 * soon as the container DOM node attaches (avoiding the "empty deps
 * array runs before the container exists" race noted in AssignView).
 *
 * `noDocument` (no row selected) renders an empty state in place of
 * the viewer. We keep the container outside the conditional render so
 * the WebViewer instance stays alive across the empty → selected
 * transition; that means the *first* document selection still triggers
 * a fresh `loadDocument()`, but subsequent selections are instant.
 *
 * `errorModal` stays disabled (Apryse's modal is styled for the matter
 * workspace and traps the triage flow), so this component owns its own
 * failure state instead. Without one, an unreadable document rendered
 * as a blank white pane with the toolbar still saying "Loading…" —
 * exactly what a soft-deleted document did on 2026-07-28, since both
 * the presign and streaming endpoints correctly 404 those.
 */
/**
 * `resolveUrl(documentId)` answers `{ url, isDirect, withCredentials, fileName,
 * contentType }` — the staff presigned-URL call by default. The embedded page
 * (/embed/document/:pass, framed inside Salesforce) passes its own: there the
 * credential is the pass, not a session.
 *
 * `editable` turns on the Annotate tools for the document on screen and saves
 * each change the way the matter workspace does (saved markup is loaded back
 * on open). Off by default: the inbox and the embedded page stay look-only.
 */
const MARKUP_SAVE_DELAY_MS = 300;
const MARKUP_RETRY_DELAYS_MS = [500, 1500];
const MARKUP_STATUS = {
  saving: "Saving markup…",
  saved: "Markup saved",
  failed: "Markup not saved yet. It will try again on your next change.",
};
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const InboxDocumentViewer = ({
  documentId = null,
  documentName = "",
  resolveUrl = getPresignedDocumentUrl,
  reportTimings = true,
  editable = false,
}) => {
  const [viewerContainer, setViewerContainer] = useState(null);
  const [viewerReady, setViewerReady] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const webViewerInstanceRef = useRef(null);
  const stopReleasingPageMemoryRef = useRef(null);
  const isViewerInitializing = useRef(false);
  const isMountedRef = useRef(false);
  const initGenerationRef = useRef(0);
  const activeDocumentIdRef = useRef(null);
  const stopwatchRef = useRef(null);
  const viewerReadyAtRef = useRef(null);
  const hasShownDocumentRef = useRef(false);
  const editableRef = useRef(editable);
  editableRef.current = editable;
  const loadedDocumentIdRef = useRef(null);
  const markupTimerRef = useRef(null);
  const unsavedMarkupRef = useRef([]);
  const markupSavingRef = useRef(null);
  const [markupStatus, setMarkupStatus] = useState(null);

  const setStatus = useCallback((value) => {
    if (isMountedRef.current) setMarkupStatus(value);
  }, []);

  // Sends every unsaved change in order; a failed one stays queued and goes
  // first next time, since the viewer hands each change over only once.
  const sendUnsavedMarkup = useCallback(async () => {
    if (markupSavingRef.current) return markupSavingRef.current;
    const run = (async () => {
      while (unsavedMarkupRef.current.length) {
        const { documentId: docId, command } = unsavedMarkupRef.current[0];
        let saved = false;
        for (let attempt = 0; attempt <= MARKUP_RETRY_DELAYS_MS.length; attempt += 1) {
          try {
            await saveDocumentAnnotations(docId, { xfdf_command: command });
            saved = true;
            break;
          } catch (err) {
            if (attempt < MARKUP_RETRY_DELAYS_MS.length) {
              await sleep(MARKUP_RETRY_DELAYS_MS[attempt]);
            }
          }
        }
        if (!saved) {
          setStatus("failed");
          return;
        }
        unsavedMarkupRef.current.shift();
      }
      setStatus("saved");
    })();
    markupSavingRef.current = run;
    try {
      await run;
    } finally {
      markupSavingRef.current = null;
    }
    return undefined;
  }, [setStatus]);

  const saveMarkupNow = useCallback(async () => {
    clearTimeout(markupTimerRef.current);
    markupTimerRef.current = null;
    const instance = webViewerInstanceRef.current;
    const docId = loadedDocumentIdRef.current;
    const manager = instance?.Core?.annotationManager;
    if (docId && typeof manager?.exportAnnotationCommand === "function") {
      try {
        const command = await manager.exportAnnotationCommand();
        if (command && /<(add|modify|delete)>\s*<[^/]/.test(command)) {
          unsavedMarkupRef.current.push({ documentId: docId, command });
        }
      } catch (err) {
        console.error("Could not read the markup to save:", err);
      }
    }
    if (unsavedMarkupRef.current.length) {
      setStatus("saving");
      await sendUnsavedMarkup();
    }
  }, [sendUnsavedMarkup, setStatus]);
  const saveMarkupNowRef = useRef(saveMarkupNow);
  saveMarkupNowRef.current = saveMarkupNow;

  // Saved markup lives in the database and, once the server has caught up,
  // in the file itself; load it from the database only while the file is
  // behind, or always for very large PDFs (same rule as the matter viewer).
  const loadSavedMarkup = useCallback(async (instance, docId) => {
    const { annotationManager, documentViewer } = instance.Core;
    if (typeof annotationManager?.importAnnotationCommand !== "function") return;
    instance.__whgImportingAnnotations = true;
    try {
      const saved = await fetchDocumentAnnotations(docId).catch(() => null);
      if (!saved || loadedDocumentIdRef.current !== docId) return;
      const large = isLargeStreamedPdf({ documentViewer });
      const command = typeof saved.xfdf_command === "string" ? saved.xfdf_command.trim() : "";
      if (command && (large || saved.has_pending_merge === true)) {
        const imported = await annotationManager.importAnnotationCommand(command);
        if (Array.isArray(imported) && imported.length) {
          await annotationManager.drawAnnotationsFromList?.(imported);
        }
      }
    } catch (err) {
      console.warn("Could not load the saved markup:", err);
    } finally {
      // What was just loaded is not a change to save.
      try {
        await annotationManager.exportAnnotationCommand?.();
      } catch {
        /* nothing to drain */
      }
      instance.__whgImportingAnnotations = false;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopwatchRef.current?.finish("abandoned");
    };
  }, []);

  // Ignore failures reported for a row the user has already navigated
  // away from: Apryse fires LOAD_ERROR asynchronously, so a slow 404 on
  // the previous document must not poison the pane for the current one.
  const reportLoadError = useCallback((forDocumentId, message) => {
    if (!isMountedRef.current) return;
    if (forDocumentId !== activeDocumentIdRef.current) return;
    setLoadError(message);
  }, []);

  // Initialize the WebViewer once the container DOM node attaches. The
  // generation token + isStale check guard against the "user assigned/
  // skipped before async init finished" race that AssignView documents
  // in detail — see `AssignView.js` for the rationale. The inbox can't
  // hit the same race quite as easily (we don't tear down between rows),
  // but the pattern is cheap and keeps the two viewers behaviorally
  // identical.
  useEffect(() => {
    if (!viewerContainer) return undefined;
    if (webViewerInstanceRef.current || isViewerInitializing.current) {
      return undefined;
    }

    const myGeneration = ++initGenerationRef.current;
    isViewerInitializing.current = true;

    WebViewer(
      {
        path: "/webviewer/lib/",
        workerPath: "/webviewer/lib/core/",
        licenseKey: process.env.REACT_APP_APRYSE_LICENSE,
        fullAPI: false,
        enableWebSocket: false,
        disabledElements: ["errorModal"],
        streaming: true,
        useDownloader: true,
      },
      viewerContainer
    )
      .then((instance) => {
        const isStale =
          !isMountedRef.current || myGeneration !== initGenerationRef.current;
        if (isStale) {
          try {
            instance.UI.dispose?.();
          } catch {
            /* cleanup best-effort */
          }
          return;
        }
        webViewerInstanceRef.current = instance;
        isViewerInitializing.current = false;

        instance.UI.setToolbarGroup("toolbarGroup-View");
        instance.Core.annotationManager.enableReadOnlyMode();
        applyApryseFeatureRestrictions(instance);
        instance.Core.annotationManager.addEventListener?.(
          "annotationChanged",
          (_annotations, _action, info) => {
            if (!editableRef.current || info?.imported || instance.__whgImportingAnnotations) {
              return;
            }
            clearTimeout(markupTimerRef.current);
            markupTimerRef.current = setTimeout(
              () => saveMarkupNowRef.current(),
              MARKUP_SAVE_DELAY_MS,
            );
          },
        );

        // Inbox is review-only — disable annotation/edit/insert/forms
        // toolgroups so attorneys triaging mail don't accidentally start
        // marking up the document. The full review/markup surface lives
        // in the matter workspace, reachable via "Open in matter".
        instance.UI.disableElements([
          "toolbarGroup-Annotate",
          "toolbarGroup-Edit",
          "toolbarGroup-Shapes",
          "toolbarGroup-Insert",
          "toolbarGroup-Forms",
          "toolbarGroup-FillAndSign",
        ]);

        instance.UI.addEventListener?.(instance.UI.Events?.LOAD_ERROR, () => {
          stopwatchRef.current?.finish("error");
          reportLoadError(
            activeDocumentIdRef.current,
            "This document couldn't be opened. It may have been deleted or "
              + "moved since it arrived in your inbox.",
          );
        });
        const { documentViewer } = instance.Core;
        stopReleasingPageMemoryRef.current = releaseOffscreenPageMemory(instance);
        documentViewer.addEventListener("documentLoaded", () => {
          if (isMountedRef.current) setLoadError(null);
          if (editableRef.current && loadedDocumentIdRef.current) {
            loadSavedMarkup(instance, loadedDocumentIdRef.current);
          }
          const stopwatch = stopwatchRef.current;
          if (stopwatch && !stopwatch.finished && stopwatch.has("loading")) {
            stopwatch.mark("opened");
            stopwatch.note({ pages: documentViewer.getPageCount?.() });
          }
        });
        // A page drawn before this load's documentLoaded belongs to the
        // previous document, hence the "opened" gate.
        const onPageDrawn = () => {
          const stopwatch = stopwatchRef.current;
          if (!stopwatch || stopwatch.finished || !stopwatch.has("opened")) return;
          hasShownDocumentRef.current = true;
          stopwatch.mark("drawn");
          stopwatch.finish("shown");
        };
        documentViewer.addEventListener("pageComplete", onPageDrawn);
        documentViewer.addEventListener("finishedRendering", onPageDrawn);

        viewerReadyAtRef.current = performance.now();
        setViewerReady(true);
      })
      .catch((err) => {
        if (myGeneration !== initGenerationRef.current) return;
        console.error("Failed to initialize Apryse WebViewer:", err);
        isViewerInitializing.current = false;
      });

    return () => {
      initGenerationRef.current += 1;
      isViewerInitializing.current = false;
      stopReleasingPageMemoryRef.current?.();
      stopReleasingPageMemoryRef.current = null;
      if (webViewerInstanceRef.current) {
        try {
          webViewerInstanceRef.current.UI.dispose?.();
        } catch {
          /* cleanup best-effort */
        }
        webViewerInstanceRef.current = null;
        viewerReadyAtRef.current = null;
        hasShownDocumentRef.current = false;
        if (isMountedRef.current) setViewerReady(false);
      }
    };
  }, [viewerContainer, reportLoadError, loadSavedMarkup]);

  useEffect(() => {
    const instance = webViewerInstanceRef.current;
    if (!instance || !viewerReady) return;
    const groups = ["toolbarGroup-Annotate"];
    if (editable) {
      instance.UI.enableElements?.(groups);
      instance.Core.annotationManager.disableReadOnlyMode?.();
    } else {
      instance.UI.setToolbarGroup("toolbarGroup-View");
      instance.UI.disableElements(groups);
      instance.Core.annotationManager.enableReadOnlyMode();
      setMarkupStatus(null);
    }
  }, [editable, viewerReady]);

  // Changes made just before leaving the card are still saved.
  useEffect(() => () => {
    if (markupTimerRef.current) saveMarkupNowRef.current();
  }, []);

  // Load (or swap) the document when `documentId` changes OR the viewer
  // becomes ready after a documentId was already set. The `viewerReady`
  // dep closes the race where documentId arrives before async Apryse
  // init finishes.
  useEffect(() => {
    const instance = webViewerInstanceRef.current;
    if (!instance || !viewerReady) return;
    const previous = stopwatchRef.current;
    if (documentId == null) {
      previous?.finish("abandoned");
      setMediaPreview(null);
      return;
    }
    // A re-run for the same letter (its name or row arriving late) keeps
    // timing the original click rather than restarting the clock.
    let stopwatch = previous;
    if (
      !previous
      || previous.finished
      || String(previous.documentId) !== String(documentId)
    ) {
      previous?.finish("abandoned");
      stopwatch = startInboxOpen(documentId, {
        viewerCold: !hasShownDocumentRef.current,
        viewerReadyAt: viewerReadyAtRef.current,
        report: reportTimings,
      });
      stopwatchRef.current = stopwatch;
    }

    // Presigned-first, streaming fallback (firehose day one,
    // 2026-07-09: inbox mail documents live in the mail system's
    // bucket, which the streaming service can't serve — the viewer
    // sat on "Loading…" forever). getPresignedDocumentUrl asks the
    // backend for a direct S3 URL against the object's real bucket
    // and falls back to the streaming proxy on any error — the same
    // contract the matter workspace viewer uses.
    let cancelled = false;
    activeDocumentIdRef.current = documentId;
    setLoadError(null);
    setMediaPreview(null);
    (async () => {
      if (markupTimerRef.current) await saveMarkupNowRef.current();
      if (cancelled) return;
      let url;
      let isDirect;
      let withCredentials = false;
      let fileName = documentName;
      let contentType = "";
      let resolvedFileName;
      let resolvedContentType;
      try {
        ({
          url,
          isDirect,
          withCredentials = false,
          fileName: resolvedFileName,
          contentType: resolvedContentType,
        } = await resolveUrl(documentId));
        fileName = resolvedFileName || documentName;
        contentType = resolvedContentType || "";
      } catch (err) {
        console.error("Could not resolve an inbox document URL:", err);
      }
      if (cancelled) return;
      stopwatch.mark("link");
      if (!url) {
        stopwatch.finish("error");
        reportLoadError(
          documentId,
          "This document couldn't be opened — the server didn't return a "
            + "location for it. It may have been deleted since it arrived.",
        );
        return;
      }

      const previewInfo = getPreviewFileInfo({
        fileName,
        contentType,
      });
      const initialFileType = previewInfo.fileType;
      stopwatch.note({ file_type: initialFileType });
      if (initialFileType === "audio" || initialFileType === "video") {
        stopwatch.cancel();
        setMediaPreview({
          kind: initialFileType,
          src: url,
          fileName: fileName || `Document ${documentId}`,
          mimeType: previewInfo.mimeType,
          withCredentials,
        });
        return;
      }

      // Email source files are converted by the backend to a read-only PDF
      // before Apryse sees them. The raw RFC/MSG bytes are not PDF content.
      if (initialFileType === "email") {
        url = getEmailPreviewUrl(documentId);
        isDirect = false;
        withCredentials = true;
        contentType = "application/pdf";
      }

      const loadOptions = {
        filename: fileName || `Document ${documentId}`,
        extension: previewInfo.extension,
        withCredentials,
      };
      if (!isDirect) {
        loadOptions.streaming = true;
      }

      stopwatch.note({ direct: Boolean(isDirect) });
      stopwatch.mark("loading");
      loadedDocumentIdRef.current = documentId;
      if (isMountedRef.current) setMarkupStatus(null);
      try {
        instance.UI.loadDocument(url, loadOptions);
      } catch (err) {
        console.error("Apryse refused the inbox document:", err);
        stopwatch.finish("error");
        reportLoadError(
          documentId,
          "This document couldn't be opened. It may have been deleted or "
            + "moved since it arrived in your inbox.",
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [documentId, documentName, viewerReady, reportLoadError, resolveUrl, reportTimings]);

  // Phase 7: subscribe to citation-jump events emitted by `BriefingMarkdown`
  // pills inside the inbox AI panel (Briefing + Context Analysis tabs).
  // The inbox viewer holds a single Apryse instance bound to the
  // currently-selected mail-intake document, so we don't need to gate
  // on a doc-id match — the bus naturally only fires while the AI
  // panel is bound to the same document the viewer is showing.
  // `viewerReady` gates the subscription so we don't try to drive the
  // viewer before WebViewer's WASM bootstrap has finished; jumps that
  // arrive during init are dropped on the floor (the user can re-click).
  useEffect(() => {
    if (!viewerReady) return undefined;
    return subscribeCitationJump((detail) => {
      if (!detail) return;
      const { page, searchQuery } = detail;
      if (!page) return;
      // The inbox surface only views one doc at a time — the citation
      // is always against the active doc, so we don't gate on
      // ``targetDocumentId`` here. New-shape ``searchQuery`` snippets
      // light up the same Apryse search-highlight path as the matter
      // surface, so inbox citations get full page+snippet behavior
      // for free.
      // Land, confirm the viewer is on the page, then highlight the
      // quote fenced to that page (same contract as the matter surface).
      landOnCitation(webViewerInstanceRef.current, { page, searchQuery });
    });
  }, [viewerReady]);

  const showEmpty = documentId == null;
  const showError = !showEmpty && Boolean(loadError);

  let toolbarLabel = documentName;
  if (!toolbarLabel) {
    if (showEmpty) toolbarLabel = "No document selected";
    else if (showError) toolbarLabel = "Unavailable";
    else toolbarLabel = "Loading…";
  }

  return (
    <InboxViewerPane aria-label="Document viewer">
      <InboxViewerToolbar>
        <InboxViewerDocName>
          <FileText size={16} weight="fill" color="#0C53BB" aria-hidden="true" />
          <span>{toolbarLabel}</span>
        </InboxViewerDocName>
        {editable && markupStatus ? (
          <span
            role="status"
            style={{
              marginLeft: "auto",
              fontSize: 12,
              color: markupStatus === "failed" ? "#C2410C" : "#5B6B80",
            }}
          >
            {MARKUP_STATUS[markupStatus]}
          </span>
        ) : null}
      </InboxViewerToolbar>

      <InboxViewerBody>
        {/* Apryse container is always mounted — do not unmount it
            between documents (would tear down WASM and cost 1-3s on
            every row click). We hide it under the empty state via CSS
            so the empty state still reads correctly. */}
        <div
          ref={setViewerContainer}
          style={{
            width: "100%",
            height: "100%",
            display: showEmpty || showError || mediaPreview ? "none" : "block",
          }}
        />
        {showEmpty && (
          <InboxViewerEmpty role="status">
            <FileText size={36} weight="duotone" color="#98A6BA" />
            <InboxViewerEmptyTitle>No document selected</InboxViewerEmptyTitle>
            <InboxViewerEmptyDescription>
              Pick an item from the list on the left to preview it here.
            </InboxViewerEmptyDescription>
          </InboxViewerEmpty>
        )}
        {showError && (
          <InboxViewerEmpty role="alert">
            <WarningCircle size={36} weight="duotone" color="#C2410C" />
            <InboxViewerEmptyTitle>Can&apos;t open this document</InboxViewerEmptyTitle>
            <InboxViewerEmptyDescription>{loadError}</InboxViewerEmptyDescription>
          </InboxViewerEmpty>
        )}
        {mediaPreview && (
          <MediaPreview
            kind={mediaPreview.kind}
            src={mediaPreview.src}
            fileName={mediaPreview.fileName}
            mimeType={mediaPreview.mimeType}
            withCredentials={mediaPreview.withCredentials}
          />
        )}
      </InboxViewerBody>
    </InboxViewerPane>
  );
};

InboxDocumentViewer.propTypes = {
  resolveUrl: PropTypes.func,
  documentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  documentName: PropTypes.string,
  editable: PropTypes.bool,
};

export default InboxDocumentViewer;
